import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useHubMembers } from "../../data/communityHub";
import CommunityHubLogo from "../community/shared/CommunityHubLogo";

const FOOTER_AVATAR_MAX = 3;

type FooterAvatar = { id: string; initial: string };

function memberFirstInitial(name?: string, email?: string) {
  const source = (name || email || "").trim();
  if (!source) return "?";
  const firstName = source.split(/\s+/)[0];
  return firstName.charAt(0).toUpperCase() || "?";
}

interface MemberAvatar {
  initials: string;
}

export interface CommunityCardData {
  id: string;
  hubName: string;
  location: string;
  descriptions: string;
  memberCount?: number;
  memberAvatars?: MemberAvatar[];
  moderatorBy?: Object;
  logoVariant?: "navy" | "teal" | "deep" | "dark";
}

const LOGO_COLORS: Record<string, string> = {
  navy: "rgb(21, 65, 94)",
  teal: "rgb(30, 89, 109)",
  deep: "rgb(18, 47, 70)",
  dark: "rgb(24, 56, 82)",
};

const DESC_FONT_SIZE = 14;
const DESC_LINE_HEIGHT = 1.45;
const DESC_MAX_LINES = 4;
const DESC_BLOCK_HEIGHT = DESC_FONT_SIZE * DESC_LINE_HEIGHT * DESC_MAX_LINES;

export default function CommunityPostCard({
  data,
  basePath = "/community",
  isAmbassador = false,
}: {
  data: CommunityCardData;
  basePath?: string;
  isAmbassador?: boolean;
}) {
  const { data: hubMembers }: any = useHubMembers(data?.id || "");
  const memberCount = hubMembers?.length ?? 0;

  const footerAvatars = useMemo(
    () =>
      (hubMembers || []).slice(0, FOOTER_AVATAR_MAX).map((member: any) => ({
        id: member.id,
        initial: memberFirstInitial(member.name, member.email),
      })),
    [hubMembers],
  ) as FooterAvatar[];

  // const { data: hubPosts }: any = useHubPosts([data.id]);

  const logoBg = LOGO_COLORS[data.logoVariant || "navy"];
  const navigate = useNavigate();
  const detailPath = `${basePath}/${data.id}`;
  const locationLabel = (data.location || "").trim();

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid rgba(138,160,174, .18)",
        borderRadius: 16,
        padding: "22px 22px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        textAlign: "left",
        width: "100%",
        height: "100%",
        minHeight: 320,
        boxSizing: "border-box",
        transition: "box-shadow .15s, transform .15s, border-color .15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow =
          "0 6px 20px rgba(30,89,109,0.06), 0 2px 4px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = "rgba(154,186,227, .8)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = "rgba(138,160,174, .18)";
      }}
      onClick={() => {
        navigate(detailPath, {
          state: {
            community: { ...data, hubMembers: memberCount },
          },
        });
      }}
    >
      {/* Cover */}
      <div
        style={{
          height: 92,
          borderRadius: 12,
          background:
            "linear-gradient(180deg, rgb(212, 232, 252) 0%, rgb(232, 240, 250) 60%, rgb(244, 247, 248) 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      />

      {/* Top: Logo + Name + Location */}
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <CommunityHubLogo
          hubName={data.hubName}
          size={52}
          background={logoBg}
        />
        <div style={{ minWidth: 0 }}>
          <h3
            style={{
              fontWeight: 700,
              fontSize: 18,
              lineHeight: 1.2,
              color: "rgb(21, 65, 94)",
              margin: "0 0 4px",
            }}
          >
            {data?.hubName}
          </h3>
          {locationLabel ? (
            <p
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontWeight: 500,
                fontSize: 13,
                lineHeight: 1,
                color: "rgb(97, 97, 97)",
                margin: 0,
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
              {locationLabel}
            </p>
          ) : null}
        </div>
      </div>

      {/* Description — fixed 4-line block; overflow shows ellipsis */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <p
          style={{
            fontWeight: 500,
            fontSize: DESC_FONT_SIZE,
            lineHeight: DESC_LINE_HEIGHT,
            color: "rgb(97, 97, 97)",
            margin: 0,
            minHeight: DESC_BLOCK_HEIGHT,
            maxHeight: DESC_BLOCK_HEIGHT,
            display: "-webkit-box",
            WebkitLineClamp: DESC_MAX_LINES,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}
        >
          {data?.descriptions || ""}
        </p>
      </div>

      {/* Ambassador badge — below description */}
      {isAmbassador && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            alignSelf: "flex-start",
            padding: "5px 12px",
            borderRadius: 999,
            background: "rgb(219, 234, 248)",
            color: "rgb(21, 65, 94)",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgb(234, 179, 8)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          You&apos;re an Ambassador
        </span>
      )}

      {/* Footer — pinned to bottom of card */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          paddingTop: 14,
          marginTop: "auto",
          flexShrink: 0,
          borderTop: "1px solid rgba(138,160,174, .18)",
        }}
      >
        {footerAvatars.length > 0 && (
          <div style={{ display: "flex", alignItems: "center" }}>
            {footerAvatars.map((avatar: FooterAvatar, index: number) => (
              <div
                key={avatar.id}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: "rgb(200, 227, 251)",
                  color: "rgb(21, 65, 94)",
                  border: "2px solid #fff",
                  fontWeight: 700,
                  fontSize: 10,
                  lineHeight: 1,
                  display: "grid",
                  placeItems: "center",
                  marginLeft: index === 0 ? 0 : -8,
                }}
              >
                {avatar.initial}
              </div>
            ))}
          </div>
        )}

        <span
          style={{
            fontWeight: 500,
            fontSize: 13,
            lineHeight: 1,
            color: "rgb(97, 97, 97)",
          }}
        >
          <b style={{ color: "rgb(21, 65, 94)", fontWeight: 700 }}>
            {memberCount}
          </b>{" "}
          members
        </span>

        {/* Open link */}
        <span
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontWeight: 600,
            fontSize: 13,
            lineHeight: 1,
            color: "rgb(21, 65, 94)",
            cursor: "pointer",
          }}
          onClick={() => {
            navigate(detailPath, {
              state: {
                community: { ...data, hubMembers: memberCount },
              },
            });
          }}
        >
          Open
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </div>
    </div>
  );
}
